<script setup lang="ts">
import type { Screen } from '../../../model/assessments/interfaces'

import { AppText } from '@shared/ui/typography'

import { usePreviewScreen } from '../../../model/assessments/preview-screen.hook'
import PreviewAuthorNote from './PreviewAuthorNote.vue'
import PreviewContentBlocks from './PreviewContentBlocks.vue'
import PreviewActivity from './PreviewActivity.vue'

/**
 * One screen: its shared content, then its activities, in document order.
 *
 * The two-column layout is named, not reproduced: the preview column is 320px wide, and faking a
 * split in it would misrepresent the player (§A-10). A screen with neither content nor activities
 * renders as a stated empty screen — what a kid would meet.
 */
defineOptions({
  name: 'PreviewScreen',
})

const props = defineProps<{
  screen: Screen
}>()

const {
  refContent,
  refHasContent,
  refActivities,
  refHasActivities,
  refIsEmpty,
  refCta,
  refAuthorNote,
} = usePreviewScreen(() => props.screen)
</script>

<template>
  <div class="preview-screen">
    <AppText
      v-if="refIsEmpty"
      name="body2"
      color="slate-500"
    >
      This screen has no content and no activities — a kid would see an empty screen.
    </AppText>

    <PreviewContentBlocks
      v-if="refHasContent"
      :blocks="refContent"
    />

    <ul
      v-if="refHasActivities"
      class="preview-screen__activities"
    >
      <li
        v-for="item in refActivities"
        :key="item.key"
      >
        <PreviewActivity
          :activity="item.activity"
          :position="item.position"
        />
      </li>
    </ul>

    <div class="preview-screen__cta">
      <button
        class="preview-screen__forward"
        type="button"
        disabled
      >
        {{ refCta || 'Next' }}
      </button>
    </div>

    <PreviewAuthorNote>
      {{ refAuthorNote }}
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewScreen.scss" />
